import {useApolloClient} from '@apollo/client';
import {useCreateUpdateLinkTokenMutation} from '@transactions/graphql';
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import * as React from 'react';
import {PlaidEmbeddedLink} from 'react-plaid-link';
import {Button} from 'src/Button';
import {SYNC_REFRESHED_QUERIES} from 'src/hooks/useSyncUpdatesAvailable';
import {cn} from 'src/lib/utils';
import {Modal} from 'src/Modal';
import type {SyncStep, SyncStreamEvent, TokenStats} from 'common/syncStream';
import type {SystemProps} from 'src/Modals';


const STEP_LABELS: Record<SyncStep, string> = {
  fetch_transactions: 'Fetch transactions',
  save_accounts: 'Save accounts',
  save_transactions: 'Save transactions',
  fetch_holdings: 'Fetch holdings',
  save_holdings: 'Save holdings',
  fetch_investment_transactions: 'Fetch investment transactions',
  save_investment_transactions: 'Save investment transactions',
};

type TokenStepEntry = {
  step: SyncStep;
  status: 'start' | 'complete' | 'error';
  detail?: string;
  timestamp: number;
};

type TokenStatus = {
  tokenId: string;
  institutionName: string;
  status: 'syncing' | 'success' | 'error';
  steps: TokenStepEntry[];
  stats?: TokenStats;
  error?: string;
  needsRelink?: boolean;
};

type SyncState = {
  phase: 'connecting' | 'syncing' | 'complete' | 'error';
  tokenCount: number;
  tokens: Map<string, TokenStatus>;
  summary?: {
    sessionId: string;
    durationMs: number;
    tokensSucceeded: number;
    tokensFailed: number;
    totalTransactionsCreated: number;
    totalAccountsCreated: number;
    totalAccountsUpdated: number;
  };
  errorMessage?: string;
};

const initialState: SyncState = {
  phase: 'connecting',
  tokenCount: 0,
  tokens: new Map(),
};

type SyncAction =
  | {type: 'SYNC_START'; tokenCount: number}
  | {type: 'TOKEN_START'; tokenId: string; institutionName: string}
  | {
      type: 'TOKEN_STEP';
      tokenId: string;
      step: SyncStep;
      status: 'start' | 'complete' | 'error';
      detail?: string;
    }
  | {type: 'TOKEN_COMPLETE'; tokenId: string; stats: TokenStats}
  | {
      type: 'TOKEN_ERROR';
      tokenId: string;
      error: string;
      needsRelink: boolean;
    }
  | {type: 'TOKEN_RELINKED'; tokenId: string}
  | {
      type: 'SYNC_COMPLETE';
      summary: NonNullable<SyncState['summary']>;
    }
  | {type: 'ERROR'; message: string};

const syncReducer = (state: SyncState, action: SyncAction): SyncState => {
  switch (action.type) {
    case 'SYNC_START':
      return {
        ...state,
        phase: 'syncing',
        tokenCount: action.tokenCount,
      };

    case 'TOKEN_START': {
      const newTokens = new Map(state.tokens);
      newTokens.set(action.tokenId, {
        tokenId: action.tokenId,
        institutionName: action.institutionName,
        status: 'syncing',
        steps: [],
      });
      return {...state, tokens: newTokens};
    }

    case 'TOKEN_STEP': {
      const newTokens = new Map(state.tokens);
      const existing = newTokens.get(action.tokenId);
      if (existing) {
        newTokens.set(action.tokenId, {
          ...existing,
          steps: [
            ...existing.steps,
            {
              step: action.step,
              status: action.status,
              detail: action.detail,
              timestamp: Date.now(),
            },
          ],
        });
      }
      return {...state, tokens: newTokens};
    }

    case 'TOKEN_COMPLETE': {
      const newTokens = new Map(state.tokens);
      const existing = newTokens.get(action.tokenId);
      if (existing) {
        newTokens.set(action.tokenId, {
          ...existing,
          status: 'success',
          stats: action.stats,
        });
      }
      return {...state, tokens: newTokens};
    }

    case 'TOKEN_ERROR': {
      const newTokens = new Map(state.tokens);
      const existing = newTokens.get(action.tokenId);
      if (existing) {
        newTokens.set(action.tokenId, {
          ...existing,
          status: 'error',
          error: action.error,
          needsRelink: action.needsRelink,
        });
      }
      return {...state, tokens: newTokens};
    }

    case 'TOKEN_RELINKED': {
      const newTokens = new Map(state.tokens);
      const existing = newTokens.get(action.tokenId);
      if (existing) {
        newTokens.set(action.tokenId, {
          ...existing,
          status: 'success',
          error: undefined,
          needsRelink: false,
        });
      }
      return {...state, tokens: newTokens};
    }

    case 'SYNC_COMPLETE':
      return {
        ...state,
        phase: 'complete',
        summary: action.summary,
      };

    case 'ERROR':
      return {
        ...state,
        phase: 'error',
        errorMessage: action.message,
      };

    default:
      return state;
  }
};

const dispatchEventAction = (
  dispatch: React.Dispatch<SyncAction>,
  event: SyncStreamEvent,
): void => {
  switch (event.type) {
    case 'sync_start':
      dispatch({type: 'SYNC_START', tokenCount: event.tokenCount});
      break;
    case 'token_start':
      dispatch({
        type: 'TOKEN_START',
        tokenId: event.tokenId,
        institutionName: event.institutionName,
      });
      break;
    case 'token_step':
      dispatch({
        type: 'TOKEN_STEP',
        tokenId: event.tokenId,
        step: event.step,
        status: event.status,
        detail: event.detail,
      });
      break;
    case 'token_complete':
      dispatch({
        type: 'TOKEN_COMPLETE',
        tokenId: event.tokenId,
        stats: event.stats,
      });
      break;
    case 'token_error':
      dispatch({
        type: 'TOKEN_ERROR',
        tokenId: event.tokenId,
        error: event.error,
        needsRelink: event.needsRelink,
      });
      break;
    case 'sync_complete':
      dispatch({
        type: 'SYNC_COMPLETE',
        summary: {
          sessionId: event.summary.sessionId,
          durationMs: event.summary.durationMs,
          tokensSucceeded: event.summary.tokensSucceeded,
          tokensFailed: event.summary.tokensFailed,
          totalTransactionsCreated: event.summary.totalTransactionsCreated,
          totalAccountsCreated: event.summary.totalAccountsCreated,
          totalAccountsUpdated: event.summary.totalAccountsUpdated,
        },
      });
      break;
    case 'error':
      dispatch({type: 'ERROR', message: event.message});
      break;
  }
};

const getLatestStepLabel = (steps: TokenStepEntry[]): string | null => {
  if (steps.length === 0) return null;
  const last = steps[steps.length - 1];
  const label = STEP_LABELS[last.step];
  if (last.status === 'start') {
    return `${label}...`;
  }
  return null;
};

type StepDisplayEntry = {
  step: SyncStep;
  label: string;
  startTime: number;
  endTime?: number;
  durationMs?: number;
  detail?: string;
  errored?: boolean;
};

const buildStepDisplay = (steps: TokenStepEntry[]): StepDisplayEntry[] => {
  const entries: StepDisplayEntry[] = [];

  for (const entry of steps) {
    if (entry.status === 'start') {
      entries.push({
        step: entry.step,
        label: STEP_LABELS[entry.step],
        startTime: entry.timestamp,
      });
    } else {
      const existing = entries.find(
        (e) => e.step === entry.step && e.endTime == null,
      );
      if (existing) {
        existing.endTime = entry.timestamp;
        existing.durationMs = entry.timestamp - existing.startTime;
        existing.detail = entry.detail;
        existing.errored = entry.status === 'error';
      }
    }
  }

  return entries;
};

const StepLog = ({steps}: {steps: TokenStepEntry[]}) => {
  const entries = buildStepDisplay(steps);

  if (entries.length === 0) return null;

  return (
    <div className="border-border/50 ml-6 border-l pl-3 pb-1">
      {entries.map((entry, i) => {
        const isTerminal = entry.endTime != null;
        const durationText =
          entry.durationMs != null
            ? `${(entry.durationMs / 1000).toFixed(1)}s`
            : null;

        return (
          <div key={i} className="flex flex-row items-baseline gap-2 py-0.5">
            <span
              className={cn(
                'text-xs',
                entry.errored
                  ? 'text-red-500'
                  : isTerminal
                    ? 'text-muted-foreground'
                    : 'text-foreground',
              )}
            >
              {entry.label}
            </span>
            {isTerminal && durationText && (
              <span className="text-muted-foreground/60 text-xs">
                {durationText}
              </span>
            )}
            {isTerminal && entry.detail && (
              <span
                className={cn(
                  'text-xs',
                  entry.errored ? 'text-red-500' : 'text-muted-foreground',
                )}
              >
                {entry.detail}
              </span>
            )}
            {!isTerminal && (
              <Loader2 className="text-muted-foreground size-3 animate-spin" />
            )}
          </div>
        );
      })}
    </div>
  );
};

const TokenRow = ({
  token,
  onRelink,
}: {
  token: TokenStatus;
  onRelink?: () => void;
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const hasSteps = token.steps.length > 0;

  const statusIcon =
    token.status === 'syncing' ? (
      <Loader2 className="text-muted-foreground size-4 animate-spin" />
    ) : token.status === 'success' ? (
      <CheckCircle className="size-4 text-green-500" />
    ) : (
      <XCircle className="size-4 text-red-500" />
    );

  const subtitleText =
    token.status === 'success' && token.stats
      ? `${token.stats.transactionsCreated} transactions`
      : token.status === 'error'
        ? token.error
        : token.status === 'syncing'
          ? getLatestStepLabel(token.steps)
          : null;

  const handleToggle = () => {
    if (hasSteps) {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <div className="py-2">
      <div
        className={cn(
          'flex flex-row items-center justify-between',
          hasSteps && 'cursor-pointer',
        )}
        onClick={handleToggle}
      >
        <div className="flex flex-row items-center gap-2">
          {hasSteps ? (
            expanded ? (
              <ChevronDown className="text-muted-foreground size-4" />
            ) : (
              <ChevronRight className="text-muted-foreground size-4" />
            )
          ) : (
            <div className="size-4" />
          )}
          {statusIcon}
          <span>{token.institutionName}</span>
        </div>
        <div className="flex flex-row items-center gap-2">
          {subtitleText && (
            <span
              className={cn(
                'text-sm',
                token.status === 'error'
                  ? 'text-red-500'
                  : 'text-muted-foreground',
              )}
            >
              {subtitleText}
            </span>
          )}
          {token.needsRelink && onRelink && (
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onRelink();
              }}
            >
              Relink
            </Button>
          )}
        </div>
      </div>
      {expanded && <StepLog steps={token.steps} />}
    </div>
  );
};

const SyncSummary = ({
  summary,
}: {
  summary: NonNullable<SyncState['summary']>;
}) => {
  const durationSec = (summary.durationMs / 1000).toFixed(1);
  return (
    <div className="border-border mt-4 border-t pt-4">
      <div className="text-muted-foreground flex flex-col gap-1 text-sm">
        <div>
          {summary.totalTransactionsCreated} new transactions,{' '}
          {summary.totalAccountsUpdated} accounts updated
        </div>
        <div>
          {summary.tokensSucceeded} succeeded, {summary.tokensFailed} failed
        </div>
        <div>Completed in {durationSec}s</div>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type SyncModalUserProps = {};

export const SyncModal = ({
  onRequestClose,
}: SyncModalUserProps & SystemProps) => {
  const apolloClient = useApolloClient();
  const [state, dispatch] = React.useReducer(syncReducer, initialState);
  const [relinkTokenId, setRelinkTokenId] = React.useState<string | null>(null);
  const [relinkLinkToken, setRelinkLinkToken] = React.useState<string | null>(
    null,
  );

  const [createUpdateLinkToken] = useCreateUpdateLinkTokenMutation();

  // Refetch relevant queries as soon as sync completes. InboxBacklogSummary
  // feeds the inbox's review count, so it must refresh too — otherwise the
  // "Review (N)" button keeps its pre-sync count until the next navigation.
  // The investments-overview set refreshes as a group: sync writes balance
  // readings and investment transactions, and refetching one card while its
  // neighbors keep pre-sync numbers would misread. (refetchQueries only
  // touches queries with mounted observers, so off-page names are free.)
  React.useEffect(() => {
    if (state.phase === 'complete') {
      void apolloClient.refetchQueries({include: [...SYNC_REFRESHED_QUERIES, 'PlaidUpdatesAvailable']});
    }
  }, [apolloClient, state.phase]);

  // Start sync when modal opens
  React.useEffect(() => {
    let aborted = false;

    const startSync = async () => {
      try {
        // Auth rides on the httpOnly session cookie (BFF); in dev the
        // Parcel proxy makes this same-origin (.proxyrc.json).
        const response = await fetch('/api/sync/stream', {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
          },
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`HTTP ${response.status}: ${text}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (!aborted) {
          const {done, value} = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, {stream: true});
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data) {
                try {
                  const event: SyncStreamEvent = JSON.parse(data);
                  dispatchEventAction(dispatch, event);
                } catch {
                  dispatch({
                    type: 'ERROR',
                    message: 'Failed to parse sync event',
                  });
                }
              }
            }
          }
        }
      } catch (error: unknown) {
        if (!aborted) {
          const message =
            error instanceof Error ? error.message : 'Unknown error';
          dispatch({type: 'ERROR', message});
        }
      }
    };

    void startSync();

    return () => {
      aborted = true;
    };
  }, []);

  const handleRelink = (tokenId: string) => {
    setRelinkTokenId(tokenId);
    createUpdateLinkToken({variables: {accessTokenId: tokenId}}).then(
      (result) => {
        if (result.data?.createUpdateLinkToken) {
          setRelinkLinkToken(result.data.createUpdateLinkToken);
        }
      },
      () => {
        setRelinkTokenId(null);
      },
    );
  };

  const handlePlaidSuccess = () => {
    // Update the token status in state to show success
    if (relinkTokenId) {
      dispatch({type: 'TOKEN_RELINKED', tokenId: relinkTokenId});
    }
    // Clear the relink state
    setRelinkTokenId(null);
    setRelinkLinkToken(null);
  };

  const handlePlaidExit = () => {
    // User exited Plaid Link without completing
    setRelinkTokenId(null);
    setRelinkLinkToken(null);
  };

  const headerText = relinkLinkToken
    ? 'Relink Account'
    : state.phase === 'connecting'
      ? 'Connecting...'
      : state.phase === 'syncing'
        ? 'Syncing Accounts'
        : state.phase === 'complete'
          ? 'Sync Complete'
          : 'Sync Error';

  const tokens = Array.from(state.tokens.values());

  // Show Plaid Link for relinking
  if (relinkLinkToken) {
    return (
      <Modal header={headerText} onRequestClose={onRequestClose} size="lg">
        <div className="h-[500px] w-full">
          <PlaidEmbeddedLink
            token={relinkLinkToken}
            onSuccess={handlePlaidSuccess}
            onExit={handlePlaidExit}
            style={{height: '100%', width: '100%'}}
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      header={headerText}
      onRequestClose={onRequestClose}
      // The header's X can be a stretch to reach on a tall mobile sheet, so
      // mirror it with a footer Close. The error phase renders its own Close
      // in-body, so skip the duplicate there.
      footer={
        state.phase !== 'error' ? (
          <Button variant="outline" onClick={onRequestClose}>
            Close
          </Button>
        ) : undefined
      }
    >
      {state.phase === 'connecting' && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="text-muted-foreground size-8 animate-spin" />
        </div>
      )}

      {state.phase === 'error' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <AlertTriangle className="size-8 text-red-500" />
          <p className="text-muted-foreground">{state.errorMessage}</p>
          <Button onClick={onRequestClose}>Close</Button>
        </div>
      )}

      {(state.phase === 'syncing' || state.phase === 'complete') && (
        <div>
          {state.tokenCount === 0 && state.phase === 'complete' ? (
            <div className="text-muted-foreground py-4 text-center">
              No connected accounts to sync.
            </div>
          ) : (
            <div className="divide-border divide-y">
              {tokens.map((token) => (
                <TokenRow
                  key={token.tokenId}
                  token={token}
                  onRelink={
                    token.needsRelink
                      ? () => handleRelink(token.tokenId)
                      : undefined
                  }
                />
              ))}
            </div>
          )}

          {state.phase === 'complete' && state.summary && (
            <SyncSummary summary={state.summary} />
          )}
        </div>
      )}
    </Modal>
  );
};
